import type { LearningRouteScope, PageAction, ExamCode } from '@examflow/learning-space';
import { buildAttemptHref, buildScopedLearningHref, getLearningModuleLabel } from '@examflow/learning-space';
import type { LearnerFeatureGates } from '@examflow/api';

export interface WorkspaceRetentionTool {
  id: 'mistake-book' | 'report' | 'achievements';
  title: string;
  description: string;
  action: PageAction;
}

/**
 * Secondary surfaces kept off the primary shell nav.
 * Diagnosis / mock live in LearningShell; nextStep remains the main driver.
 */
export function buildWorkspaceRetentionTools(
  scope: LearningRouteScope | null | undefined,
  gates?: Partial<LearnerFeatureGates> | null
): WorkspaceRetentionTool[] {
  const tools: WorkspaceRetentionTool[] = [
    {
      id: 'mistake-book',
      title: '错题本',
      description: '归档浏览。到期短训走页面主按钮；解析仍在训练结果页。',
      action: {
        id: 'open-mistake-book',
        label: '打开错题本',
        kind: 'ghost',
        href: buildScopedLearningHref('/mistake-book', scope),
      },
    },
    {
      id: 'report',
      title: '训练统计',
      description: '历史正确率与连胜。能力结论与处方以诊断页为准。',
      action: {
        id: 'open-learning-report',
        label: '查看统计',
        kind: 'ghost',
        href: buildScopedLearningHref('/report', scope),
      },
    },
    {
      id: 'achievements',
      title: '成就',
      description: '徽章与里程碑。留存激励，不进入主训练闭环。',
      action: {
        id: 'open-achievements',
        label: '查看成就',
        kind: 'ghost',
        href: buildScopedLearningHref('/achievements', scope),
      },
    },
  ];

  return tools.filter((tool) => {
    if (tool.id === 'mistake-book') return gates?.mistakeBook !== false;
    if (tool.id === 'report') return gates?.abilityReport !== false;
    return true;
  });
}

export function buildRetentionToolBackAction(
  scope: LearningRouteScope | null | undefined
): PageAction {
  return {
    id: 'back-to-workspace',
    label: '返回工作台',
    kind: 'secondary',
    href: buildScopedLearningHref('/workspace', scope),
  };
}

/** Dominant Skill among due mistakes — drives remediation short-train CTA. */
export function resolveDominantMistakeSkillId(
  mistakes: Array<{ question?: { section?: string | null } | null }>
): string | null {
  const counts = new Map<string, number>();
  for (const mistake of mistakes) {
    const skill = String(mistake.question?.section || '')
      .trim()
      .toLowerCase();
    if (!skill || skill === 'diagnosis' || skill === 'result') continue;
    counts.set(skill, (counts.get(skill) ?? 0) + 1);
  }

  let best: string | null = null;
  let bestCount = 0;
  for (const [skill, count] of counts) {
    if (count > bestCount) {
      best = skill;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Mistake book primary CTA:
 * - due + Skill → remediation short train (closed loop)
 * - otherwise → ability diagnosis (archive / no focus)
 */
export function buildRetentionMistakeBookPrimaryAction(
  scope: LearningRouteScope | null | undefined,
  input: { dueCount: number; focusSkillId?: string | null }
): PageAction {
  const focusSkillId = input.focusSkillId?.trim().toLowerCase() || null;
  const examType = scope?.examType?.trim().toLowerCase() || null;

  if (input.dueCount > 0 && focusSkillId && examType) {
    try {
      const focusLabel = getLearningModuleLabel(examType as ExamCode, focusSkillId);
      return {
        id: 'start-remediation-short-training',
        label: `重做${focusLabel}短训`,
        kind: 'primary',
        href: buildAttemptHref({
          kind: 'remediation',
          languageId: scope?.languageId,
          examType: examType as ExamCode,
          skillId: focusSkillId,
          intensity: 'short',
          reviewFocus: focusSkillId,
          next: buildScopedLearningHref('/diagnosis/ability', scope),
        }),
      };
    } catch {
      // Fall through to diagnosis when AttemptSpec cannot be built.
    }
  }

  return {
    id: 'open-ability-diagnosis',
    label: '查看能力诊断',
    kind: 'primary',
    href: buildScopedLearningHref('/diagnosis/ability', scope),
  };
}

export function buildRetentionReportPrimaryAction(
  scope: LearningRouteScope | null | undefined
): PageAction {
  return {
    id: 'open-ability-diagnosis',
    label: '查看能力诊断',
    kind: 'primary',
    href: buildScopedLearningHref('/diagnosis/ability', scope),
  };
}
